import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Save, User, Phone, Building2, MapPin, Globe, Pencil, X, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileEditor({ onProfileUpdate }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/profile`, { headers })
      .then(r => { setProfile(r.data); setForm(r.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      for (const key of ["full_name", "phone", "company", "bio", "location", "website"]) {
        if (form[key] !== profile[key] && form[key] !== undefined) {
          updates[key] = form[key] || null;
        }
      }
      if (Object.keys(updates).length === 0) {
        setEditing(false);
        return;
      }
      const res = await axios.put(`${API}/profile`, updates, { headers });
      setProfile(res.data.user);
      setForm(res.data.user);
      setEditing(false);
      toast.success("Profile updated");
      if (onProfileUpdate) onProfileUpdate(res.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally { setSaving(false); }
  };

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;
  if (!profile) return null;

  const role = profile.role;
  const initials = (profile.full_name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="space-y-5" data-testid="profile-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-emerald-700 text-white flex items-center justify-center text-lg font-bold">{initials}</div>
          <div>
            <h2 className="text-lg font-bold text-[#0A0A0A]" data-testid="profile-name">{profile.full_name}</h2>
            <div className="flex items-center gap-2 text-xs text-[#888]">
              <span>{profile.email}</span>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700">{role}</span>
              {profile.verification_status === "verified" && (
                <span className="flex items-center gap-0.5 text-emerald-600"><CheckCircle className="h-3 w-3" /> Verified</span>
              )}
            </div>
          </div>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} data-testid="edit-profile-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition-colors">
            <Pencil className="h-3.5 w-3.5" /> Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { setEditing(false); setForm(profile); }} data-testid="cancel-edit-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-cream-warm text-xs font-medium text-[#666] hover:bg-cream transition-colors">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button onClick={handleSave} disabled={saving} data-testid="save-profile-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
            </button>
          </div>
        )}
      </div>

      {/* Profile Fields */}
      <div className="grid sm:grid-cols-2 gap-4">
        <ProfileField icon={User} label="Full Name" value={form.full_name} editing={editing}
          onChange={v => update("full_name", v)} testId="profile-full-name" />
        <ProfileField icon={Phone} label="Phone" value={form.phone} editing={editing}
          onChange={v => update("phone", v)} testId="profile-phone" />
        <ProfileField icon={Building2} label="Company / Entity" value={form.company} editing={editing}
          onChange={v => update("company", v)} testId="profile-company" />
        <ProfileField icon={MapPin} label="Location" value={form.location} editing={editing}
          onChange={v => update("location", v)} testId="profile-location" />
        <ProfileField icon={Globe} label="Website" value={form.website} editing={editing}
          onChange={v => update("website", v)} testId="profile-website" />
      </div>

      {/* Bio */}
      <div className="rounded-xl border border-cream-warm bg-cream p-4">
        <label className="text-[10px] uppercase tracking-wider text-[#999] mb-2 block">Bio / About</label>
        {editing ? (
          <textarea value={form.bio || ""} onChange={e => update("bio", e.target.value)} rows={3}
            placeholder="Tell us about yourself or your business..."
            data-testid="profile-bio-input"
            className="w-full rounded-lg border border-[#e0d8c8] bg-white p-3 text-sm text-[#333] placeholder:text-[#bbb] focus:outline-none focus:border-emerald-400 resize-y" />
        ) : (
          <p className="text-sm text-[#333]" data-testid="profile-bio">{profile.bio || <span className="text-[#bbb] italic">No bio added yet</span>}</p>
        )}
      </div>

      {/* Role-Specific Stats */}
      {profile.stats && (
        <div className="rounded-xl border border-cream-warm bg-white p-4">
          <h3 className="text-xs uppercase tracking-wider text-[#999] mb-3 font-semibold">Account Stats</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {role === "borrower" && <>
              <StatCard label="Active Deals" value={profile.stats.active_deals || 0} />
              <StatCard label="Funded Deals" value={profile.stats.funded_deals || 0} accent />
              <StatCard label="Total Volume" value={fmtUSD(profile.stats.total_volume || 0)} />
              <StatCard label="Documents" value={profile.stats.documents_uploaded || 0} />
            </>}
            {(role === "lender" || role === "investor") && <>
              <StatCard label="Deals Reviewed" value={profile.stats.deals_reviewed || 0} />
              <StatCard label="Term Sheets" value={profile.stats.term_sheets_submitted || 0} />
              <StatCard label="Accepted" value={profile.stats.term_sheets_accepted || 0} accent />
              <StatCard label="Total Offered" value={fmtUSD(profile.stats.total_offered || 0)} />
            </>}
          </div>
        </div>
      )}

      {/* Member Since */}
      <div className="text-[10px] text-[#bbb] text-center pt-2">
        Member since {profile.created_at ? new Date(profile.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "—"}
      </div>
    </div>
  );
}

function ProfileField({ icon: Icon, label, value, editing, onChange, testId }) {
  return (
    <div className="rounded-xl border border-cream-warm bg-cream p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5 text-[#888]" />
        <label className="text-[10px] uppercase tracking-wider text-[#999]">{label}</label>
      </div>
      {editing ? (
        <input value={value || ""} onChange={e => onChange(e.target.value)}
          data-testid={`${testId}-input`}
          className="w-full rounded-lg border border-[#e0d8c8] bg-white px-3 py-2 text-sm text-[#333] focus:outline-none focus:border-emerald-400" />
      ) : (
        <p className="text-sm font-medium text-[#333]" data-testid={testId}>{value || <span className="text-[#bbb]">—</span>}</p>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="text-center">
      <div className={`text-lg font-bold ${accent ? "text-emerald-600" : "text-[#0A0A0A]"}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-[#999]">{label}</div>
    </div>
  );
}

function fmtUSD(n) {
  if (!n) return "$0";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
